import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

// Calls
import {
  changeSubscriptionPlanRequest,
  cancelSubscribeRequest
} from "../../../../api/calls/subscription.call";

// Actions
import { fetchActiveSubscription } from "../../../../redux/actions/business.actions";
import { fetchAuthenticatedUserData } from "../../../../redux/actions/app.actions";

// Billing UI
import {
  BillingModal,
  InlineError,
  Spinner,
  money,
  fmtStamp,
  fmtStampShort
} from "./shared";
import { I } from "./icons";

const CONTACT_EMAIL = "sales@truckfix.net";

/* Enterprise — contact us */
export const ContactModal = ({ onClose }) => (
  <BillingModal
    title="Talk to us about Enterprise"
    sub="Tell us about your operation and we'll tailor pricing for unlimited locations."
    onClose={onClose}
    footer={
      <>
        <button className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <a
          className="btn btn-primary"
          href={`mailto:${CONTACT_EMAIL}?subject=Enterprise plan inquiry`}
        >
          <I.Mail width="14" height="14" /> Email us
        </a>
      </>
    }
  >
    <p className="mdl-text">
      Enterprise covers franchises and national fleet accounts with{" "}
      <b>unlimited locations</b> and all features included. Reach out and our
      team will get back to you within one business day.
    </p>
    <div className="mdl-callout">
      <I.Mail width="14" height="14" /> {CONTACT_EMAIL}
    </div>
  </BillingModal>
);

/* Change plan (up/downgrade) — reuses the saved card, no checkout */
export const ChangePlanModal = ({ fromPlan, toPlan, onClose }) => {
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const upgrade =
    (toPlan.price?.unitAmount || 0) > (fromPlan?.price?.unitAmount || 0);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    const response = await changeSubscriptionPlanRequest(toPlan);
    setBusy(false);
    if (response.hasError) {
      // The BE also rejects downgrades below the active-branch count.
      setError(response.error.message);
      return;
    }
    toast.success(`Your plan was changed to ${toPlan.name}.`);
    onClose();
    setTimeout(() => {
      dispatch(fetchActiveSubscription());
      // Plan capabilities ride on dashboard_data — refresh so locks follow.
      dispatch(fetchAuthenticatedUserData());
    }, 1000);
  };

  return (
    <BillingModal
      title={`Switch from ${fromPlan?.name} to ${toPlan.name}?`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Keep {fromPlan?.name}
          </button>
          <button className="btn btn-primary" onClick={confirm} disabled={busy}>
            {busy ? (
              <>
                <Spinner /> Switching…
              </>
            ) : (
              "Confirm change"
            )}
          </button>
        </>
      }
    >
      <div className="mdl-plan-compare">
        <div className="mdl-plan-chip">
          <span>{fromPlan?.name}</span>
          <b>{fromPlan?.price ? money(fromPlan.price.unitAmount) + "/mo" : "—"}</b>
        </div>
        <span className="mdl-arrow">
          <I.ArrowRight />
        </span>
        <div className="mdl-plan-chip to">
          <span>{toPlan.name}</span>
          <b>{toPlan.price ? money(toPlan.price.unitAmount) + "/mo" : "—"}</b>
        </div>
      </div>
      <p className="mdl-text">
        {upgrade ? (
          <>
            Your card will be charged a <b>prorated amount</b> for the rest of
            this billing period, then{" "}
            <b>{money(toPlan.price.unitAmount)}/mo</b> afterward. Reuses your
            saved card — no checkout needed.
          </>
        ) : (
          <>
            You'll receive <b>prorated credit</b> on your next invoice, then pay{" "}
            <b>{money(toPlan.price.unitAmount)}/mo</b>. The change takes effect
            immediately.
          </>
        )}
      </p>
      {error && <InlineError style={{ marginTop: 14 }}>{error}</InlineError>}
    </BillingModal>
  );
};

/* Cancel subscription / trial (at period end) */
export const CancelModal = ({ activeSubscription, onClose }) => {
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const trial = activeSubscription?.status === "trialing";
  const endStamp = activeSubscription?.currentPeriodEnd;
  const planName = activeSubscription?.item?.subscriptionPlan?.name;

  const confirm = async () => {
    setBusy(true);
    const response = await cancelSubscribeRequest();
    setBusy(false);
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    toast.success(trial ? "Trial canceled." : "Subscription canceled.");
    onClose();
    setTimeout(() => dispatch(fetchActiveSubscription()), 1000);
  };

  return (
    <BillingModal
      title={trial ? "Cancel your free trial?" : "Cancel subscription?"}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Keep subscription
          </button>
          <button className="btn btn-danger" onClick={confirm} disabled={busy}>
            {busy ? (
              <>
                <Spinner /> Canceling…
              </>
            ) : trial ? (
              "Cancel trial"
            ) : (
              "Cancel subscription"
            )}
          </button>
        </>
      }
    >
      <p className="mdl-text">
        {trial ? (
          <>
            You'll keep access until <b>{fmtStamp(endStamp)}</b> and won't be
            charged. After that your locations go offline for drivers.
          </>
        ) : (
          <>
            Your <b>{planName}</b> plan will stay active until{" "}
            <b>{fmtStamp(endStamp)}</b>. After that your locations go offline
            for drivers and your team loses access.
          </>
        )}
      </p>
      <div className="mdl-callout">
        <I.Info width="14" height="14" /> You can resume anytime before{" "}
        {fmtStampShort(endStamp)} to stay subscribed.
      </div>
    </BillingModal>
  );
};
